import { useCallback, useEffect, useState } from 'react'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import { useReorderViews } from '../../services/api/overview.hooks'
import {
    TViewMoveAnchor,
    anchorViewId,
    getCardMoveAnchors,
    getDropAnchor,
    moveViewToAnchor,
} from '../../utils/overview'
import { DropItem, TOverviewView } from '../../utils/types'

export interface OverviewCardOrderActions {
    // absent where the card cannot move any further
    moveToTop?: () => void
    moveUp?: () => void
    moveDown?: () => void
}

const ANNOUNCEMENT_MS = 4000

// Daily Overview card order is the only routine order, so every card reorders where it sits.
const useOverviewCardOrder = (lists: TOverviewView[]) => {
    const { mutate: reorderView } = useReorderViews()
    // Read-only accounts cannot save a move, so do not offer one that would snap back.
    const isReadOnly = useIsReadOnly()
    const [announcement, setAnnouncement] = useState('')

    useEffect(() => {
        if (!announcement) return
        const timeout = setTimeout(() => setAnnouncement(''), ANNOUNCEMENT_MS)
        return () => clearTimeout(timeout)
    }, [announcement])

    const move = useCallback(
        (view: TOverviewView, anchor: TViewMoveAnchor) => {
            if (anchorViewId(anchor) === view.id) return
            const position = moveViewToAnchor(lists, view.id, anchor).findIndex((list) => list.id === view.id)
            // Dropping a card back where it was is not a move.
            if (position === lists.findIndex((list) => list.id === view.id)) return
            reorderView({ id: view.id, anchor }, view.optimisticId)
            setAnnouncement(`${view.name} moved to ${position + 1} of ${lists.length}`)
        },
        [lists, reorderView]
    )

    // A card added a moment ago still has a temporary client id the server cannot anchor to.
    const canAnchorTo = useCallback(
        (anchor: TViewMoveAnchor) =>
            !isReadOnly && !lists.find((list) => list.id === anchorViewId(anchor))?.optimisticId,
        [lists, isReadOnly]
    )

    const getActions = useCallback(
        (index: number): OverviewCardOrderActions => {
            const view = lists[index]
            const anchors = getCardMoveAnchors(
                lists.map((list) => list.id),
                index
            )
            const action = (anchor?: TViewMoveAnchor) =>
                anchor && canAnchorTo(anchor) ? () => move(view, anchor) : undefined
            return { moveToTop: action(anchors.toTop), moveUp: action(anchors.up), moveDown: action(anchors.down) }
        },
        [lists, move, canAnchorTo]
    )

    const onDrop = useCallback(
        (item: DropItem, slot: number) => {
            const view = lists.find((list) => list.id === item.id)
            const anchor = getDropAnchor(
                lists.map((list) => list.id),
                slot
            )
            if (view && anchor && canAnchorTo(anchor)) move(view, anchor)
        },
        [lists, move, canAnchorTo]
    )

    return { getActions, onDrop, announcement }
}

export default useOverviewCardOrder
